import { OverlayModule } from '@angular/cdk/overlay';
import { DatePipe } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';

import {
    RDS_ICON_LOADER_CONFIG,
    RdsAlertModule,
    RdsAvatarModule,
    RdsBadgeModule,
    RdsButtonModule,
    RdsCheckboxModule,
    RdsChipsModule,
    RdsDatepickerModule,
    RdsDialogModule,
    RdsDividerModule,
    RdsDropdownModule,
    RdsFormFieldModule,
    RdsHeaderModule,
    RdsIconLoaderConfig,
    RdsIconsModule,
    RdsMenuModule,
    RdsPaginatorModule,
    RdsProgressBarModule,
    RdsProgressSpinnerModule,
    RdsRadioButtonModule,
    RdsSidenavModule,
    RdsTableModule,
    RdsToastModule,
    RdsTooltipModule,
} from '@rds/angular-components';

import {
    DopHeaderComponent,
    OnlyLoggedInGuard,
    SdhHeaderComponent,
    SpdgCoreModule,
    authInterceptor,
} from '@spdg/core';
import { OAuthModule } from 'angular-oauth2-oidc';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { provideLottieOptions } from 'ngx-lottie';
import { MARKED_OPTIONS, MarkdownModule } from 'ngx-markdown';

import { FormsModule } from '@angular/forms';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { InternalComponentsModule } from './component/internal-components.module';
import { ProductSelectorComponent } from './component/product-selector/product-selector.component';
import { ContentBlockScrollDirective } from './directives/content-block-scroll/content-block-scroll.directive';
import { EmptyLayoutComponent } from './layout/empty-layout/empty-layout.component';
import { MainLayoutComponent } from './layout/main-layout/main-layout.component';
import { ProfileSettingsComponent } from './modules/designer/components/profile-settings/profile-settings.component';
import { CommonHeaderComponent } from './page/common-header/common-header.component';
import { ConfirmComponent } from './page/confirm/confirm.component';
import { ErrorGlobalPageComponent } from './page/error-global-page/error-global-page.component';
import { LoadingPageComponent } from './page/loading-page/loading-page.component';
import { LoginComponent } from './page/login/login.component';
import { ProgressLoaderComponent } from './page/progress-loader/progress-loader.component';
import { RolesDescriptionComponent } from './page/share/roles-description/roles-description.component';
import { ShareComponent } from './page/share/share.component';
import { DisclaimerDialogComponent } from './page/start/disclaimer-dialog/disclaimer-dialog.component';
import { NoAccessDialogComponent } from './page/start/no-access-dialog/no-access-dialog.component';
import { SessionExpiredDialogComponent } from './page/start/session-expired-dialog/session-expired-dialog.component';
import { ToastComponent } from './page/toast/toast.component';
import { NotifyService } from './service/notify.service';
import { TermsOfServiceConfiguration } from './terms-of-service.config';
import { authCodeFlowConfig } from './utility/auth-flow';
import { markedOptionsFactory } from './utility/factories/marked-options.factory';
import { TruncatePipe } from './utility/pipes/truncate.pipe';

@NgModule({
    declarations: [
        AppComponent,
        MainLayoutComponent,
        EmptyLayoutComponent,
        LoginComponent,
        LoadingPageComponent,
        ErrorGlobalPageComponent,
        DisclaimerDialogComponent,
        SessionExpiredDialogComponent,
        ProgressLoaderComponent,
        ToastComponent,
        ConfirmComponent,
        ContentBlockScrollDirective,
        ProductSelectorComponent,
        NoAccessDialogComponent,
        CommonHeaderComponent,
        ShareComponent,
        RolesDescriptionComponent,
        ProfileSettingsComponent,
    ],
    imports: [
        AppRoutingModule,
        AngularSvgIconModule.forRoot(),
        BrowserModule,
        FormsModule,
        InternalComponentsModule,
        MarkdownModule.forRoot({
            markedOptions: {
                provide: MARKED_OPTIONS,
                useFactory: markedOptionsFactory,
            },
        }),
        OAuthModule.forRoot(),
        OverlayModule,
        RdsAlertModule,
        RdsAvatarModule,
        RdsBadgeModule,
        RdsTooltipModule,
        RdsButtonModule,
        RdsCheckboxModule,
        RdsChipsModule,
        RdsDialogModule,
        RdsDividerModule,
        RdsHeaderModule,
        RdsIconsModule,
        RdsMenuModule,
        RdsProgressBarModule,
        RdsProgressSpinnerModule,
        RdsSidenavModule,
        RdsToastModule,
        RdsDropdownModule,
        RdsFormFieldModule,
        RdsRadioButtonModule,
        RdsPaginatorModule,
        RdsTableModule,
        RdsDatepickerModule,
        SpdgCoreModule.forRoot(
            import.meta.env.NG_APP_FE_SUITE,
            TermsOfServiceConfiguration,
            authCodeFlowConfig,
            {
                applicationCode: 'SDH',
                isLandingPageMode: false,
                landingPageUrl: import.meta.env.NG_APP_LANDING_PAGE_URL,
            },
            {
                absoluteIdleMs: 25 * 24 * 60 * 60 * 1000, // 25 days,
                shortIdleMs: 100 * 365 * 24 * 60 * 60 * 1000, // effectively disabled
            }
        ),
        DopHeaderComponent,
        SdhHeaderComponent,
        TruncatePipe,
    ],
    bootstrap: [AppComponent],

    providers: [
        NotifyService,
        DatePipe,
        RdsDatepickerModule,

        {
            provide: RDS_ICON_LOADER_CONFIG,
            useValue: {
                preloadSelected: 'all',
            } as RdsIconLoaderConfig,
        },

        provideHttpClient(withInterceptors([authInterceptor])),
        provideAnimations(),
        OnlyLoggedInGuard,
        provideLottieOptions({ player: () => import('lottie-web') }),
    ],
})
export class AppModule {}
